import {
  useMonitoringEvents,
  useMonitoringEventsSummary,
} from "@babackapp/private-api-client/client/use-super-admin-monitoring-events";
import type { MonitoringEventDto } from "@babackapp/private-api-client/types";
import { Button } from "@babackapp/ui/components/button";
import { Input } from "@babackapp/ui/components/input";
import { ChevronDown, ChevronRight, Search, X } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useDebounce } from "use-debounce";
import { usePersistedSearchParams } from "./admin-preferences";
import { AdminPageShell } from "./admin-tab-pages";
import { formatRelative } from "./shop-shared";

/** `category` of the journal: the `SlackChannel` key, `general` without one. */
const CATEGORY_LABELS: Record<string, string> = {
  jobs: "Jobs",
  carriers: "Transporteurs",
  stripe: "Stripe",
  emails: "Emails",
  shops: "Boutiques",
  users: "Utilisateurs",
  security: "Sécurité",
  rgpd: "RGPD",
  general: "Général",
};

type Severity = MonitoringEventDto["severity"];
type Outcome = NonNullable<MonitoringEventDto["notificationOutcome"]>;

const SEVERITIES: { value: Severity; label: string }[] = [
  { value: "ERROR", label: "Erreurs" },
  { value: "WARNING", label: "Avertissements" },
  { value: "INFO", label: "Infos" },
];

const SEVERITY_STYLES: Record<Severity, { dot: string; badge: string }> = {
  ERROR: { dot: "bg-red-500", badge: "bg-red-50 text-red-700 ring-red-200" },
  WARNING: {
    dot: "bg-amber-500",
    badge: "bg-amber-50 text-amber-800 ring-amber-200",
  },
  INFO: {
    dot: "bg-emerald-500",
    badge: "bg-emerald-50 text-emerald-800 ring-emerald-200",
  },
};

const OUTCOMES: Record<Outcome, { label: string; className: string }> = {
  SENT_BOT: { label: "Posté sur Slack", className: "text-muted-foreground" },
  SENT_WEBHOOK: {
    label: "Posté (webhook)",
    className: "text-muted-foreground",
  },
  THROTTLED: {
    label: "Non posté : limite anti-spam",
    className: "text-muted-foreground",
  },
  FAILED: { label: "Échec d'envoi Slack", className: "text-red-700" },
  NOT_CONFIGURED: {
    label: "Slack non configuré",
    className: "text-amber-800",
  },
  MUTED: {
    label: "Non posté : coupée pour cette boutique",
    className: "text-muted-foreground",
  },
};

export function categoryLabel(category: string): string {
  return CATEGORY_LABELS[category] ?? category;
}

function formatDateTime(date: string): string {
  return new Intl.DateTimeFormat("fr-FR", {
    dateStyle: "short",
    timeStyle: "medium",
  }).format(new Date(date));
}

/** Consecutive occurrences of one problem, shown as one row. */
type EventGroup = { first: MonitoringEventDto; events: MonitoringEventDto[] };

function groupKey(event: MonitoringEventDto): string {
  return event.key ?? `${event.category}|${event.title}|${event.shopId ?? ""}`;
}

function groupConsecutive(events: MonitoringEventDto[]): EventGroup[] {
  const groups: EventGroup[] = [];
  for (const event of events) {
    const last = groups[groups.length - 1];
    if (last && groupKey(last.first) === groupKey(event)) {
      last.events.push(event);
    } else {
      groups.push({ first: event, events: [event] });
    }
  }
  return groups;
}

/**
 * The monitoring journal (`monitoringEvent`, kept a year): every alert sent to
 * Slack, posted or held back by the throttle, and the events recorded without
 * a notification. Global under /admin/events, one shop's under
 * /admin/shops/:shopId/events.
 */
export function AdminMonitoringEventsPage() {
  const { shopId } = useParams<{ shopId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get("category") ?? "";
  const severity = (searchParams.get("severity") ?? undefined) as
    | Severity
    | undefined;
  const outcome = (searchParams.get("outcome") ?? undefined) as
    | Outcome
    | undefined;
  const key = searchParams.get("key") ?? "";
  usePersistedSearchParams("events", { exclude: ["key"] });
  const urlSearch = searchParams.get("q") ?? "";
  const [search, setSearch] = useState(urlSearch);
  // Filters restored from this browser land after the first render: follow
  // the URL when it changes (adjusting state during render, not in an effect).
  const [syncedSearch, setSyncedSearch] = useState(urlSearch);
  if (urlSearch !== syncedSearch) {
    setSyncedSearch(urlSearch);
    setSearch(urlSearch);
  }
  const [debouncedSearch] = useDebounce(search, 300);
  const [isGrouped, setIsGrouped] = useState(true);

  function setParam(name: string, value: string | null) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(name, value);
        else next.delete(name);
        return next;
      },
      { replace: true },
    );
  }

  const summary = useMonitoringEventsSummary({ hours: 24, shopId });
  const events = useMonitoringEvents({
    category,
    severity,
    outcome,
    key,
    shopId,
    q: debouncedSearch,
  });
  const rows = useMemo(
    () => events.data?.pages.flatMap((page) => page.events) ?? [],
    [events.data],
  );
  const groups = useMemo(
    () =>
      isGrouped && !key
        ? groupConsecutive(rows)
        : rows.map((event) => ({ first: event, events: [event] })),
    [rows, isGrouped, key],
  );

  return (
    <AdminPageShell
      title={shopId ? "Journal de la boutique" : "Journal & alertes"}
      description="Activité, erreurs et anomalies de la plateforme, gardées un an : chaque alerte Slack (#monitor-*), postée ou retenue par la limite anti-spam, et les événements enregistrés sans notification."
    >
      <section>
        <h2 className="mb-2 text-sm font-semibold text-foreground">
          Dernières 24 h
        </h2>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-5">
          {summary.data?.categories.length === 0 && (
            <p className="col-span-full text-sm text-muted-foreground">
              Aucun événement sur les dernières 24 h.
            </p>
          )}
          {summary.data?.categories.map((item) => (
            <button
              key={item.category}
              type="button"
              onClick={() =>
                setParam(
                  "category",
                  category === item.category ? null : item.category,
                )
              }
              className={[
                "rounded-lg border bg-background p-3 text-left transition-colors hover:border-violet-400",
                category === item.category
                  ? "border-violet-500 ring-1 ring-violet-500"
                  : "",
              ].join(" ")}
            >
              <div className="truncate text-xs font-medium text-muted-foreground">
                {categoryLabel(item.category)}
              </div>
              <div className="mt-1 text-2xl font-semibold tabular-nums text-foreground">
                {item.events.toLocaleString("fr-FR")}
              </div>
              <div className="flex gap-2 text-xs">
                {item.errors > 0 && (
                  <span className="font-medium text-red-700">
                    {item.errors} erreur{item.errors > 1 ? "s" : ""}
                  </span>
                )}
                {item.warnings > 0 && (
                  <span className="font-medium text-amber-800">
                    {item.warnings} avert.
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      </section>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setParam("q", event.target.value || null);
            }}
            placeholder="Rechercher dans le titre ou l'erreur…"
            className="h-9 w-80 bg-background pl-9"
          />
        </div>
        <select
          value={category}
          onChange={(event) => setParam("category", event.target.value || null)}
          className="h-9 rounded-md border bg-background px-3 text-sm text-foreground"
        >
          <option value="">Toutes les catégories</option>
          {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <div className="flex overflow-hidden rounded-md border bg-background">
          <button
            type="button"
            onClick={() => setParam("severity", null)}
            className={`px-3 py-1.5 text-sm ${!severity ? "bg-violet-600 text-white" : "text-foreground hover:bg-muted"}`}
          >
            Tous
          </button>
          {SEVERITIES.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              onClick={() => setParam("severity", value)}
              className={`border-l px-3 py-1.5 text-sm ${severity === value ? "bg-violet-600 text-white" : "text-foreground hover:bg-muted"}`}
            >
              {label}
            </button>
          ))}
        </div>
        <select
          value={outcome ?? ""}
          onChange={(event) => setParam("outcome", event.target.value || null)}
          className="h-9 rounded-md border bg-background px-3 text-sm text-foreground"
        >
          <option value="">Toute notification</option>
          {Object.entries(OUTCOMES).map(([value, { label }]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        {!key && (
          <label className="flex items-center gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              checked={isGrouped}
              onChange={(event) => setIsGrouped(event.target.checked)}
              className="h-4 w-4 accent-violet-600"
            />
            Regrouper les répétitions
          </label>
        )}
      </div>

      {key && (
        <div className="flex items-center justify-between gap-3 rounded-md border border-violet-200 bg-violet-50 px-4 py-2 text-sm text-violet-900">
          <span className="truncate">
            Toutes les occurrences de <code className="font-mono">{key}</code>
          </span>
          <button
            type="button"
            onClick={() => setParam("key", null)}
            className="inline-flex shrink-0 items-center gap-1 font-medium hover:underline"
          >
            <X className="h-3.5 w-3.5" /> Retirer
          </button>
        </div>
      )}

      <div className="overflow-hidden rounded-lg border bg-background">
        {events.isPending ? (
          <p className="p-6 text-sm text-muted-foreground">Chargement…</p>
        ) : events.isError ? (
          <p className="p-6 text-sm text-destructive">
            Impossible de charger le journal.
          </p>
        ) : groups.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground">
            Aucun événement pour ces filtres.
          </p>
        ) : (
          <ul className="divide-y">
            {groups.map((group) => (
              <EventRow
                key={group.first.id}
                group={group}
                isShopScoped={!!shopId}
                onShowOccurrences={(value) => setParam("key", value)}
              />
            ))}
          </ul>
        )}
      </div>

      {events.hasNextPage && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            onClick={() => void events.fetchNextPage()}
            disabled={events.isFetchingNextPage}
          >
            {events.isFetchingNextPage
              ? "Chargement…"
              : "Événements plus anciens"}
          </Button>
        </div>
      )}
    </AdminPageShell>
  );
}

function EventRow({
  group: { first: event, events },
  isShopScoped,
  onShowOccurrences,
}: {
  group: EventGroup;
  isShopScoped: boolean;
  onShowOccurrences: (key: string) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const style = SEVERITY_STYLES[event.severity];
  const outcome = event.notificationOutcome
    ? OUTCOMES[event.notificationOutcome]
    : null;
  const hasDetails = (event.details?.length ?? 0) > 0 || !!event.error;
  const oldest = events[events.length - 1];

  return (
    <li className="px-4 py-3">
      <div className="flex items-start gap-3">
        <button
          type="button"
          onClick={() => setIsOpen((open) => !open)}
          disabled={!hasDetails}
          className="mt-0.5 text-muted-foreground hover:text-foreground disabled:invisible"
          aria-label={isOpen ? "Masquer le détail" : "Voir le détail"}
        >
          {isOpen ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </button>
        <span
          className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${style.dot}`}
          aria-hidden
        />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-foreground">{event.title}</p>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <span title={formatDateTime(event.createdAt)}>
              {formatRelative(new Date(event.createdAt).getTime())}
            </span>
            <span
              className={`rounded px-1.5 py-0.5 font-medium ring-1 ring-inset ${style.badge}`}
            >
              {categoryLabel(event.category)}
            </span>
            {events.length > 1 && (
              <span
                className="font-medium text-foreground"
                title={`Première de la série : ${formatDateTime(oldest.createdAt)}`}
              >
                ×{events.length} d'affilée
              </span>
            )}
            {event.shopId && !isShopScoped && (
              <Link
                to={`/admin/shops/${event.shopId}/events`}
                className="font-medium text-violet-700 hover:underline"
              >
                {event.shopName ?? event.shopId}
              </Link>
            )}
            {outcome && (
              <span className={outcome.className}>
                {outcome.label}
                {event.notificationChannel &&
                  event.notificationOutcome?.startsWith("SENT") &&
                  ` · ${event.notificationChannel}`}
              </span>
            )}
            {event.key && (
              <button
                type="button"
                onClick={() => onShowOccurrences(event.key ?? "")}
                className="font-medium text-violet-700 hover:underline"
              >
                Toutes les occurrences
              </button>
            )}
          </div>
          {isOpen && hasDetails && (
            <div className="mt-3 space-y-2">
              {event.details && event.details.length > 0 && (
                <dl className="grid grid-cols-1 gap-x-6 gap-y-1 text-sm md:grid-cols-[max-content_1fr]">
                  {event.details.map((detail, index) => (
                    <div key={index} className="contents">
                      <dt className="font-medium text-muted-foreground">
                        {detail.title}
                      </dt>
                      <dd className="whitespace-pre-wrap break-words text-foreground">
                        {detail.value}
                      </dd>
                    </div>
                  ))}
                </dl>
              )}
              {event.error && (
                <pre className="max-h-64 overflow-auto rounded-md bg-slate-900 p-3 text-xs text-slate-100">
                  {event.error}
                </pre>
              )}
            </div>
          )}
        </div>
      </div>
    </li>
  );
}
